/**
 * Halloween tattoos — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const halloweenTattoosPack: ToilePack = {
    slug: 'halloween-tattoos',
    category: 'season',
    name: 'Halloween tattoos',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'bat', label: 'Bat', subject: 'A small bat with wings spread'},
        {key: 'mooncat', label: 'Moon and cat', subject: 'A crescent moon with a black cat sitting in it'},
        {key: 'pumpkin', label: 'Tiny pumpkin', subject: 'A tiny carved pumpkin'},
    ],
    styles: [
        {key: 'fineline', label: 'Fine line', style: 'fine single-needle black line work, thin uniform stroke, no shading, no color', palette: 'Black ink only', framing: 'Small scale, centered with a wide white margin'},
        {key: 'traditional', label: 'Bold traditional', style: 'bold American traditional flash-sheet outline, thick uniform black line, no shading, no color', palette: 'Black ink only', framing: 'Centered with a wide white margin, classic flash-sheet scale'},
        {key: 'dotwork', label: 'Dotwork', style: 'fine dotwork stippled shading over a thin black outline, stippled dot-gradient fill, no color', palette: 'Black ink, stippled dot shading', framing: 'Centered with a wide white margin'},
    ],
    directions: ['a matching pair design', 'as a tiny minimalist mark', 'a spider web instead'],
    directionHint: 'try: a matching pair design',
    seo: {
        title: 'Halloween Tattoo Ideas: Bat, Cat, Pumpkin',
        metaDescription: 'Nine halloween tattoo designs — a small bat, a crescent moon with a cat, a tiny pumpkin — fine line, black ink, no skin, three styles.',
        h1: 'Halloween tattoo ideas: bat, cat, pumpkin',
        tagline: 'Nine designs, three October subjects, three styles each.',
        intro: 'A flash sheet of small October tattoo ideas: a small bat, a crescent moon with a cat, a tiny pumpkin, drawn fine-line in black ink with no skin shown. Ask for a spider web, a raven, or your own subject, and the same three styles carry it.',
        useCases: [
            'a reference to bring to a tattoo artist',
            "a small flash sheet for a shop's Halloween board",
            'a temporary-tattoo design for a party',
            'an idea board for a seasonal piece',
        ],
        faq: [
            {q: 'What does this pack actually look like?', a: 'Nine tattoo-style designs — a small bat, a crescent moon with a cat, a tiny pumpkin — fine-line black ink on a plain background, no skin, in three styles.'},
            {q: 'Is this an actual tattoo, or a design reference?', a: "A design reference — it's artwork to bring to or discuss with a tattoo artist, not a photo of a real tattoo."},
            {q: 'Can I use this as a stencil for tattooing?', a: 'The line weight and detail are built for the design stage; a tattoo artist will typically adapt it for their own stencil and skin placement.'},
            {q: 'Can I get a different subject, like a spider web or raven?', a: 'Yes — the direction field takes any Halloween subject, including a spider web or a raven.'},
            {q: 'Can a tattoo shop use these designs on its flash sheet?', a: 'Yes — check the license terms on your account for the specifics of commercial use.'},
        ],
    },
}
